import type { BaseClient } from '../../http/BaseClient.js';
import { projectAdapter } from '../../adapters/projectAdapter.js';
import type {
  AddMemberRequest,
  CreateProjectRequest,
  ProductKind,
  Project,
  UpdateMemberRoleRequest,
  UpdateProjectRequest,
} from './types.js';

interface SimpleResponse {
  success: boolean;
  message?: string;
}

export class ProjectsClient {
  constructor(private readonly http: BaseClient) {}

  /**
   * Lists the caller's projects in one product partition. Omitting `product`
   * defaults to 'cloud' server-side (the cloud UI). The BI frontend passes
   * 'bi' so it only ever sees data projects.
   */
  async list(product?: ProductKind): Promise<Project[]> {
    const path = product ? `/project?product=${encodeURIComponent(product)}` : '/project';
    const res = await this.http.get<unknown>(path);
    return projectAdapter.fromListResponse(res);
  }

  async get(projectId: string): Promise<Project> {
    const res = await this.http.get<unknown>(`/project/${projectId}`);
    return projectAdapter.fromGetResponse(res);
  }

  async create(body: CreateProjectRequest): Promise<Project> {
    const res = await this.http.post<unknown>('/project', { body });
    return projectAdapter.fromGetResponse(res);
  }

  async update(projectId: string, body: UpdateProjectRequest): Promise<Project> {
    const res = await this.http.put<unknown>(`/project/${projectId}`, { body });
    return projectAdapter.fromGetResponse(res);
  }

  delete(projectId: string): Promise<SimpleResponse> {
    return this.http.delete<SimpleResponse>(`/project/${projectId}`);
  }

  addMember(projectId: string, body: AddMemberRequest): Promise<SimpleResponse> {
    return this.http.post<SimpleResponse>(`/project/${projectId}/users`, { body });
  }

  updateMemberRole(projectId: string, userId: string, body: UpdateMemberRoleRequest): Promise<SimpleResponse> {
    return this.http.put<SimpleResponse>(`/project/${projectId}/users/${userId}`, { body });
  }

  removeMember(projectId: string, userId: string): Promise<SimpleResponse> {
    return this.http.delete<SimpleResponse>(`/project/${projectId}/users/${userId}`);
  }
}
